import { TWhatsAppMessage } from "../store"

/**
 * Conversation helpers, kept out of the component files that use them:
 * exporting a plain function beside a component costs that module its Fast
 * Refresh state on every edit.
 */

export function getMessageDeliveryStatus(
	message: TWhatsAppMessage
): TWhatsAppMessage["status"] {
	return message.status
}

export function formatWhatsAppStatusLabel(
	status: TWhatsAppMessage["status"]
): string {
	switch (status) {
		case "accepted":
			return "Accepted"
		case "sent":
			return "Sent"
		case "delivered":
			return "Delivered"
		case "read":
			return "Read"
		case "failed":
			return "Failed"
	}
	return "N/A"
}

export function formatWhatsAppPricingCategory(
	category: TWhatsAppMessage["pricing_category"]
): string {
	switch (category) {
		case "marketing":
			return "Marketing"
		case "marketing_lite":
			return "Marketing Lite"
		case "utility":
			return "Utility"
		case "authentication":
			return "Authentication"
		case "authentication_international":
			return "Authentication (International)"
		case "service":
			return "Service"
	}
	return "N/A"
}

export function formatWhatsAppPricingType(
	type: TWhatsAppMessage["pricing_type"]
): string {
	switch (type) {
		case "regular":
			return "Regular"
		case "free_customer_service":
			return "Within the 24h service window"
		case "free_entry_point":
			return "Free entry point (ad / CTA)"
	}
	return "N/A"
}

/**
 * Meta only accepts free-form messages within 24 hours of the customer's last
 * inbound message — the "customer service window". Outside it, only an
 * approved template is delivered; free text comes back as a failed status.
 */
export const CUSTOMER_SERVICE_WINDOW_HOURS = 24

/**
 * Whether free-form messages to this thread will be rejected.
 *
 * A thread the customer has never written to counts as outside the window:
 * the window opens on their message, so a first outbound has to be a template.
 */
export function isOutsideCustomerServiceWindow(
	lastInboundAt?: string | null
): boolean {
	if (!lastInboundAt) {
		return true
	}
	const lastInbound = new Date(lastInboundAt).getTime()
	if (Number.isNaN(lastInbound)) {
		return true
	}
	return (
		Date.now() - lastInbound > CUSTOMER_SERVICE_WINDOW_HOURS * 60 * 60 * 1000
	)
}
